import { getApiUrl } from './api';
import { apiFetch } from './apiFetch';
import {
  CATALOG_PENDING_OVERRIDES_KEY,
  CATALOG_PRODUCT_CACHE_KEY,
  CATALOG_SELLABLE_ITEMS_CACHE_KEY,
  getCatalogStorageSession,
  isCatalogStorageSessionCurrent,
  readCatalogSessionJson,
  removeCatalogSessionValue,
  writeCatalogSessionJson,
} from './catalogSessionStorage';

const IFOOD_IMAGE_BASE_URL = 'https://static-images.ifood.com.br/image/upload/';
const IFOOD_IMAGE_DEFAULT_TRANSFORM = 't_medium';
const IFOOD_IMAGE_DEFAULT_FOLDER = 'pratos';

const ensureShowInKds = (value) => value !== false;
const ensureShowInBalcao = (value) => value !== false;
const SHARED_CATALOG_MAX_JSON_CHARACTERS = 512 * 1024;
const PIZZA_GROUP_ROLES = new Set(['SIZE', 'TOPPING', 'FLAVOR', 'CRUST', 'EDGE', 'DOUGH']);

const normalizeRoleText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

const parseCatalogNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return null;
  const normalized = value
    .replace(/[^\d,.-]/g, '')
    .replace(/\.(?=\d{3}(?:\D|$))/g, '')
    .replace(',', '.');
  if (!normalized || normalized === '-' || normalized === '.' || normalized === ',') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const resolvePriceValue = (value) => {
  if (value && typeof value === 'object') {
    const candidates = [
      value.value,
      value.originalValue,
      value.currentPrice,
      value.delivery,
      value.amount,
      value.price,
    ];
    for (const candidate of candidates) {
      const parsed = parseCatalogNumber(candidate);
      if (parsed !== null) return parsed;
    }
    return null;
  }
  return parseCatalogNumber(value);
};

export const getOptionGroupId = (group = {}) => {
  const safeGroup = group && typeof group === 'object' ? group : {};
  return String(
    safeGroup.optionGroupId
  || safeGroup.groupId
  || safeGroup.id
  || safeGroup.name
  || 'group'
  ).trim();
};

export const getOptionId = (option = {}) => {
  const safeOption = option && typeof option === 'object' ? option : {};
  return String(
    safeOption.optionId
  || safeOption.customizationOptionId
  || safeOption.id
  || safeOption.productId
  || safeOption.itemId
  || ''
  ).trim();
};

export const normalizeOptionGroupRole = (group = {}) => {
  const safeGroup = group && typeof group === 'object' ? group : {};
  const explicit = String(safeGroup.optionGroupType || safeGroup.type || safeGroup.pizzaRole || safeGroup.groupType || '')
    .trim()
    .toUpperCase();
  if (PIZZA_GROUP_ROLES.has(explicit)) {
    return explicit === 'TOPPING' ? 'FLAVOR' : explicit;
  }

  const text = normalizeRoleText(`${safeGroup.name || ''} ${safeGroup.optionGroupName || ''}`);
  if (text.includes('tamanho') || text.includes('size')) return 'SIZE';
  if (text.includes('sabor') || text.includes('flavor') || text.includes('recheio')) return 'FLAVOR';
  if (text.includes('massa') || text.includes('dough')) return 'DOUGH';
  if (text.includes('borda') || text.includes('edge')) return 'EDGE';
  if (text.includes('crosta') || text.includes('crust')) return 'CRUST';
  return explicit || '';
};

const parseLimitValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const firstLimitValue = (...values) => {
  for (const value of values) {
    const parsed = parseLimitValue(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

export const normalizeOptionGroupLimits = (group = {}) => {
  const safeGroup = group && typeof group === 'object' ? group : {};
  const raw = safeGroup.raw && typeof safeGroup.raw === 'object' ? safeGroup.raw : {};
  const relation = safeGroup.relation || raw.relation || safeGroup.productRelation || raw.productRelation || {};
  const minQuantity = Math.max(0, firstLimitValue(
    safeGroup.minQuantity,
    safeGroup.minimum,
    safeGroup.min,
    safeGroup.minPermitted,
    relation.minQuantity,
    relation.minimum,
    relation.min,
    relation.minPermitted,
    raw.minQuantity,
    raw.minimum,
    raw.min,
    raw.minPermitted,
    0
  ) || 0);
  const maxRaw = firstLimitValue(
    safeGroup.maxQuantity,
    safeGroup.maximum,
    safeGroup.max,
    safeGroup.maxPermitted,
    relation.maxQuantity,
    relation.maximum,
    relation.max,
    relation.maxPermitted,
    raw.maxQuantity,
    raw.maximum,
    raw.max,
    raw.maxPermitted
  );
  const maxQuantity = maxRaw === null ? 0 : Math.max(0, maxRaw);
  return { minQuantity, maxQuantity, min: minQuantity, max: maxQuantity };
};

export const isPizzaCatalogItem = (item = {}) => {
  if (!item || typeof item !== 'object') return false;
  const safeItem = item;
  if (safeItem.isPizza === true) return true;
  const payload = safeItem.payload && typeof safeItem.payload === 'object' ? safeItem.payload : {};
  const groups = safeItem.itemOptionGroups || safeItem.optionGroups || payload.itemOptionGroups || payload.optionGroups || [];
  const groupRoles = new Set((Array.isArray(groups) ? groups : []).map((group) => normalizeOptionGroupRole(group)));
  const nameText = normalizeRoleText(`
    ${safeItem.itemName || safeItem.name || payload.itemName || ''}
    ${safeItem.categoryName || payload.categoryName || ''}
  `);
  const candidates = [
    safeItem.itemType,
    safeItem.type,
    safeItem.categoryTemplate,
    safeItem.template,
    safeItem.category?.template,
    safeItem.category?.type,
    safeItem.raw?.type,
    payload.itemType,
    payload.type,
    payload.categoryTemplate,
    payload.category?.template,
  ];
  return candidates.some((value) => String(value || '').trim().toUpperCase() === 'PIZZA')
    || Boolean(safeItem.pizzaMetadata || payload.pizzaMetadata)
    || (nameText.includes('pizza') && groupRoles.has('SIZE') && groupRoles.has('FLAVOR'));
};

const getModifierEntries = (item = {}) => {
  const payload = item.payload || {};
  if (Array.isArray(item.customizationModifiers)) return item.customizationModifiers;
  if (Array.isArray(payload.customizationModifiers)) return payload.customizationModifiers;
  if (Array.isArray(item.raw?.customizationModifiers)) return item.raw.customizationModifiers;
  return [];
};

const buildCustomizationModifierIndex = (item = {}) => {
  const index = new Map();
  getModifierEntries(item).forEach((modifier) => {
    if (!modifier || typeof modifier !== 'object') return;
    const childKeys = [
      modifier.customizationOptionId,
      modifier.optionId,
      modifier.productId,
      modifier.externalCode,
      modifier.itemExternalCode,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    if (!childKeys.length) return;

    const price = resolvePriceValue(modifier.price);
    const entry = {
      parentOptionId: String(
        modifier.parentCustomizationOptionId
        || modifier.parentOptionId
        || modifier.parentId
        || ''
      ).trim(),
      externalCode: String(modifier.externalCode || modifier.itemExternalCode || '').trim(),
      status: modifier.status || modifier.availability || '',
      price: price !== null ? price : 0,
    };

    childKeys.forEach((key) => {
      if (!index.has(key)) index.set(key, []);
      index.get(key).push(entry);
    });
  });
  return index;
};

const resolveContextualPricesForOption = (option = {}, modifierIndex = new Map()) => {
  const keys = [
    getOptionId(option),
    option.externalCode,
    option.itemExternalCode,
    option.integrationCode,
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
  const entries = keys.flatMap((key) => modifierIndex.get(key) || []);
  if (!entries.length) return option.contextualPrices || {};

  return entries.reduce((acc, entry) => {
    if (!entry.parentOptionId) return acc;
    acc[entry.parentOptionId] = {
      price: Number(entry.price || 0),
      status: entry.status || '',
      externalCode: entry.externalCode || option.externalCode || '',
    };
    return acc;
  }, { ...(option.contextualPrices || {}) });
};

export const enrichOptionGroupsWithCatalogContext = (groups = [], item = {}) => {
  const modifierIndex = buildCustomizationModifierIndex(item);
  return (Array.isArray(groups) ? groups : []).map((group) => {
    const optionGroupId = getOptionGroupId(group);
    const role = normalizeOptionGroupRole(group);
    const limits = normalizeOptionGroupLimits(group);
    return {
      ...group,
      ...limits,
      optionGroupId,
      optionGroupType: group.optionGroupType || group.type || role || '',
      pizzaRole: role,
      options: (Array.isArray(group.options) ? group.options : []).map((option) => {
        const optionId = getOptionId(option);
        const contextualPrices = resolveContextualPricesForOption(option, modifierIndex);
        return {
          ...option,
          optionId,
          contextualPrices,
        };
      }),
    };
  });
};

const getStaticOptionPrice = (option = {}) => (
  resolvePriceValue(option.price)
  ?? resolvePriceValue(option.itemPrice)
  ?? resolvePriceValue(option.unitPrice)
  ?? 0
);

export const resolveOptionUnitPrice = (product = {}, option = {}, selectedOptions = []) => {
  const staticPrice = getStaticOptionPrice(option);
  if (!isPizzaCatalogItem(product)) return staticPrice;

  const contextualPrices = option.contextualPrices || {};
  const sizeOptions = (selectedOptions || []).filter((selected) => (
    normalizeOptionGroupRole(selected) === 'SIZE'
    || selected.groupType === 'SIZE'
    || selected.pizzaRole === 'SIZE'
  ));

  for (const size of sizeOptions) {
    const keys = [
      size.optionId,
      getOptionId(size),
      size.id,
      size.externalCode,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);
    for (const key of keys) {
      const contextual = contextualPrices[key];
      if (contextual === undefined) continue;
      const parsed = resolvePriceValue(contextual);
      if (parsed !== null) return parsed;
    }
  }

  return staticPrice;
};

export const resolveSelectedOptionsForProduct = (product = {}, selectedByGroup = {}) => {
  const selected = [];
  const groups = Array.isArray(product.itemOptionGroups) ? product.itemOptionGroups : [];

  groups.forEach((group) => {
    const groupId = getOptionGroupId(group);
    const role = normalizeOptionGroupRole(group);
    const selectedMap = selectedByGroup[groupId] || {};
    Object.values(selectedMap).forEach((selectedOption) => {
      const original = (group.options || []).find((option) => {
        const selectedId = getOptionId(selectedOption);
        return (
          (selectedId && selectedId === getOptionId(option))
          || (selectedOption.externalCode && selectedOption.externalCode === option.externalCode)
          || (selectedOption.name && selectedOption.name === option.name)
        );
      }) || {};

      selected.push({
        ...original,
        ...selectedOption,
        optionId: getOptionId(selectedOption) || getOptionId(original) || null,
        externalCode: selectedOption.externalCode || original.externalCode || null,
        name: selectedOption.name || original.name || '',
        description: selectedOption.description || original.description || '',
        quantity: Number(selectedOption.quantity || 1),
        groupId,
        groupName: group.name || selectedOption.groupName || '',
        groupType: role,
        pizzaRole: role,
        minQuantity: normalizeOptionGroupLimits(group).minQuantity ?? selectedOption.minQuantity ?? 0,
        maxQuantity: normalizeOptionGroupLimits(group).maxQuantity ?? selectedOption.maxQuantity ?? 0,
        contextualPrices: selectedOption.contextualPrices || original.contextualPrices || {},
      });
    });
  });

  return selected.map((option) => ({
    ...option,
    price: resolveOptionUnitPrice(product, option, selected),
  }));
};

export const calculateSelectedOptionsTotal = (product = {}, selectedOptions = []) => {
  if (!isPizzaCatalogItem(product)) {
    return (selectedOptions || []).reduce(
      (acc, option) => acc + Number(option.price || 0) * Number(option.quantity || 1),
      0
    );
  }

  let total = 0;
  const flavorGroups = new Map();
  (selectedOptions || []).forEach((option) => {
    const role = option.groupType || option.pizzaRole || normalizeOptionGroupRole(option);
    const price = Number(option.price || 0);
    const quantity = Math.max(1, Number(option.quantity || 1));
    if (role === 'FLAVOR') {
      const groupId = option.groupId || option.groupName || 'flavor';
      if (!flavorGroups.has(groupId)) flavorGroups.set(groupId, []);
      for (let index = 0; index < quantity; index += 1) {
        flavorGroups.get(groupId).push(price);
      }
      return;
    }
    total += price * quantity;
  });

  flavorGroups.forEach((prices) => {
    total += prices.length ? Math.max(...prices) : 0;
  });
  return total;
};

export const resolveDisplayOptionPrice = (product = {}, group = {}, option = {}, selectedByGroup = {}) => {
  const selected = resolveSelectedOptionsForProduct(product, selectedByGroup);
  return resolveOptionUnitPrice(
    product,
    {
      ...option,
      groupId: getOptionGroupId(group),
      groupName: group.name || '',
      groupType: normalizeOptionGroupRole(group),
      pizzaRole: normalizeOptionGroupRole(group),
    },
    selected
  );
};

const readJsonFromLocalStorage = (key, fallback) => {
  return readCatalogSessionJson(key, fallback);
};

// A conservative bound avoids serializing multi-megabyte inline images just to
// discover that localStorage cannot hold them. Only optional display caches use
// this budget; pending mutations retain their existing persistence behavior.
const fitsJsonBudget = (value, limit) => {
  let remaining = limit;
  const ancestors = new Set();
  const visit = (entry) => {
    if (typeof entry === 'string') {
      // JSON escaping takes at most six UTF-16 characters per input character.
      remaining -= 2 + entry.length * 6;
    } else if (entry && typeof entry === 'object') {
      if (ancestors.has(entry)) return false;
      ancestors.add(entry);
      remaining -= 2;
      for (const [key, child] of Object.entries(entry)) {
        if (!Array.isArray(entry) && !visit(key)) return false;
        remaining -= 2;
        if (!visit(child)) return false;
      }
      ancestors.delete(entry);
    } else {
      remaining -= 32;
    }
    return remaining >= 0;
  };
  return visit(value);
};

const writeJsonToLocalStorage = (key, value, maxCharacters, session) => {
  if (typeof window === 'undefined') return false;
  try {
    if (maxCharacters && !fitsJsonBudget(value, maxCharacters)) {
      removeCatalogSessionValue(key, session);
      return false;
    }
    return writeCatalogSessionJson(key, value, session);
  } catch (error) {
    console.warn(`Cache local ${key} indisponível; seguindo sem persistir no navegador.`, error);
    try {
      removeCatalogSessionValue(key, session);
    } catch {
      // Ignore cleanup failure; the backend remains the source of truth.
    }
    return false;
  }
};

const normalizeUuid = (value) => {
  const normalized = String(value || '').trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(normalized)
    ? normalized
    : '';
};

const buildCatalogPriority = (catalog = {}, index = 0) => {
  const context = String(
    catalog.context
    || catalog.catalogContext
    || catalog.type
    || catalog.channel
    || ''
  ).trim().toUpperCase();
  const status = String(
    catalog.status
    || catalog.state
    || catalog.availability
    || catalog.catalogStatus
    || ''
  ).trim().toUpperCase();
  const contexts = ['DEFAULT', 'WHITELABEL', 'INDOOR', 'DELIVERY', 'TAKEOUT'];
  const contextIndex = contexts.indexOf(context);
  const activeBoost = ['AVAILABLE', 'ACTIVE', 'ENABLED'].includes(status) ? 0 : 100;
  const contextBoost = contextIndex === -1 ? 50 : contextIndex;
  return activeBoost + contextBoost + index;
};

const extractMerchantListPayload = (payload) => (
  Array.isArray(payload) ? payload : payload?.merchants
);

const resolveIfoodCatalogContext = async (apiUrl) => {
  const settingsResponse = await apiFetch(`${apiUrl}/api/settings/ifood`);
  if (!settingsResponse.ok) {
    throw new Error('Falha ao carregar configuração do iFood.');
  }

  const settings = await settingsResponse.json();
  if (settings?.integrationEnabled === false) {
    throw new Error('Integração iFood desativada nas configurações.');
  }

  let merchantIds = [
    ...(Array.isArray(settings?.merchantIds) ? settings.merchantIds : []),
    ...(Array.isArray(settings?.detectedMerchantIds) ? settings.detectedMerchantIds : []),
  ]
    .map((item) => normalizeUuid(item))
    .filter(Boolean);

  if (!merchantIds.length) {
    const merchantsResponse = await apiFetch(`${apiUrl}/api/ifood/merchants`);
    if (!merchantsResponse.ok) {
      throw new Error('Falha ao listar merchants do iFood.');
    }

    const merchantsPayload = await merchantsResponse.json();
    merchantIds = (Array.isArray(extractMerchantListPayload(merchantsPayload)) ? extractMerchantListPayload(merchantsPayload) : [])
      .flatMap((merchant) => [merchant?.id, merchant?.merchantId])
      .map((item) => normalizeUuid(item))
      .filter(Boolean);
  }

  const merchantId = merchantIds[0];
  if (!merchantId) {
    throw new Error('Nenhum merchant autorizado foi encontrado para esta integração iFood.');
  }

  const catalogsResponse = await apiFetch(`${apiUrl}/api/ifood/catalog/merchants/${merchantId}/catalogs`);
  if (!catalogsResponse.ok) {
    throw new Error('Falha ao listar catálogos do merchant no iFood.');
  }

  const catalogsPayload = await catalogsResponse.json();
  const catalogs = Array.isArray(catalogsPayload?.catalogs) ? catalogsPayload.catalogs : [];
  const catalog = catalogs
    .map((item, index) => ({
      raw: item,
      id: normalizeUuid(item?.catalogId || item?.id),
      priority: buildCatalogPriority(item, index),
    }))
    .filter((item) => item.id)
    .sort((a, b) => a.priority - b.priority)[0];

  if (!catalog?.id) {
    throw new Error('Nenhum catálogo válido foi encontrado para o merchant autorizado.');
  }

  return {
    merchantId,
    catalogId: catalog.id,
    catalog: catalog.raw,
  };
};

const cleanImageValue = (value) => String(value || '').trim();

const extractCatalogImagePath = (data, visited = new WeakSet()) => {
  if (!data) return '';
  if (typeof data === 'string') return cleanImageValue(data);
  if (Array.isArray(data)) {
    for (const item of data) {
      const extracted = extractCatalogImagePath(item, visited);
      if (extracted) return extracted;
    }
    return '';
  }
  if (typeof data !== 'object') return '';
  if (visited.has(data)) return '';
  visited.add(data);

  const directCandidates = [
    data.imagePath,
    data.path,
    data.url,
    data.fileName,
    data.logoUrl,
    data.imageUrl,
    data.logosUrls,
    data.imageUrls,
    data.images,
  ];

  for (const candidate of directCandidates) {
    const extracted = extractCatalogImagePath(candidate, visited);
    if (extracted) return extracted;
  }

  const nestedCandidates = [
    data.image,
    data.product,
    data.productData,
    data.rawProduct,
    data.raw,
  ];

  for (const candidate of nestedCandidates) {
    if (candidate && candidate !== data) {
      const extracted = extractCatalogImagePath(candidate, visited);
      if (extracted) return extracted;
    }
  }

  return '';
};

const normalizeImageList = (...sources) => {
  const seen = new Set();
  return sources.flatMap((source) => (Array.isArray(source) ? source : [source]))
    .map((value) => (typeof value === 'string' ? cleanImageValue(value) : ''))
    .filter(Boolean)
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
};

const buildIfoodImageUrl = (source) => {
  const path = extractCatalogImagePath(source);
  if (!path) {
    return null;
  }

  if (
    path.startsWith('http://')
    || path.startsWith('https://')
    || path.startsWith('data:')
  ) {
    return path;
  }
  if (path.startsWith('/api/product-media/') || path.startsWith('/api/products/order-display-image/')) {
    return `${getApiUrl()}${path}`;
  }

  const normalizedPath = path.replace(/^\/+/, '');
  const transformMatch = normalizedPath.match(/^(t_[^/]+)\/(.*)$/);
  const transform = transformMatch ? transformMatch[1] : IFOOD_IMAGE_DEFAULT_TRANSFORM;
  let remainder = transformMatch ? transformMatch[2] : normalizedPath;

  const hasFolderPrefix = remainder.startsWith('pratos/') || remainder.startsWith('logos/') || remainder.startsWith('menus/');
  if (!hasFolderPrefix) {
    remainder = `${IFOOD_IMAGE_DEFAULT_FOLDER}/${remainder}`;
  }

  return `${IFOOD_IMAGE_BASE_URL}${transform}/${remainder}`;
};

export const resolveCatalogImageUrl = (...sources) => {
  for (const source of sources) {
    const imageUrl = buildIfoodImageUrl(source);
    if (imageUrl) return imageUrl;
  }
  return null;
};

export const normalizePreparationDescription = (value) => {
  if (typeof value !== 'string') return '';

  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&#10;|&#13;&#10;|&#13;/gi, '\n')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u00a0/g, ' ')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n')
    .trim();
};

export const normalizeKdsName = (value) => (typeof value === 'string' ? value.trim() : '');

export const getPreparationDescriptionLines = (value) => {
  const normalized = normalizePreparationDescription(value);
  return normalized ? normalized.split('\n').filter(Boolean) : [];
};

const fetchCatalogOverrides = async (apiUrl, { signal } = {}) => {
  const response = await apiFetch(`${apiUrl}/api/products/catalog-overrides`, { signal });
  if (!response.ok) {
    throw new Error('Falha ao carregar overrides do catálogo');
  }
  const data = await response.json();
  return data.overrides || [];
};

export const fetchCatalogOverridesSafely = async (apiUrl, options = {}) => {
  try {
    return await fetchCatalogOverrides(apiUrl, options);
  } catch (error) {
    console.warn('Overrides do catálogo indisponíveis; usando catálogo base.', error);
    return [];
  }
};

export const fetchInventoryProducts = async (apiUrl = getApiUrl(), { signal } = {}) => {
  const response = await apiFetch(`${apiUrl}/api/products/inventory`, { signal });
  if (!response.ok) {
    throw new Error(`Erro HTTP ${response.status}: ${response.statusText}`);
  }
  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Erro ao carregar estoque importado');
  }
  return data.products || [];
};

export const fetchOrderDisplayCatalog = async (apiUrl = getApiUrl(), { signal } = {}) => {
  const response = await apiFetch(`${apiUrl}/api/products/order-display-catalog`, { signal });
  if (!response.ok) throw new Error(`Erro HTTP ${response.status}: catálogo de exibição indisponível`);
  const data = await response.json();
  if (!data.success || !Array.isArray(data.products) || !Array.isArray(data.options) || !Array.isArray(data.images)) {
    throw new Error('Resposta inválida do catálogo de exibição');
  }
  const catalog = { products: Object.create(null), options: Object.create(null) };
  const images = data.images.map((path) => resolveCatalogImageUrl(path));
  ['products', 'options'].forEach((kind) => {
    data[kind].forEach((item) => {
      if (!item?.externalCode) return;
      const entry = {
        externalCode: item.externalCode,
        merchantId: item.merchantId || '',
        pdvLinked: item.pdvLinked === true,
        name: item.name,
        description: item.description,
        imageUrl: Number.isInteger(item.imageIndex) ? (images[item.imageIndex] || null) : null,
      };
      if (entry.merchantId) catalog[kind][`${entry.merchantId}::${entry.externalCode}`] = entry;
      if (!catalog[kind][entry.externalCode]) catalog[kind][entry.externalCode] = entry;
    });
  });
  return catalog;
};

const normalizePrintIdentity = (value) => String(value || '').trim();

export const getIfoodOrderMerchantId = (order = {}) => normalizePrintIdentity(
  order?.merchant?.id
  || order?.merchant?.merchantId
  || order?.fullOrderData?.merchant?.id
  || order?.fullOrderData?.merchantId
  || order?.lastPayload?.merchantId
  || order?.merchantId
);

export const getRawIfoodOrderItems = (order = {}) => {
  const candidates = [
    order?.fullOrderData?.items,
    order?.lastPayload?.order?.items,
    order?.lastPayload?.items,
    order?.items,
  ];
  return candidates.find((items) => Array.isArray(items) && items.length > 0)
    || candidates.find(Array.isArray)
    || [];
};

export const projectOrderDisplayItem = (item = {}, catalog = {}, merchantId = '') => {
  const productKey = merchantId && item.externalCode ? `${merchantId}::${item.externalCode}` : '';
  const matchedProduct = item.externalCode
    ? (catalog.products?.[productKey] || catalog.products?.[item.externalCode])
    : null;
  const projectedOptions = Array.isArray(item.options)
    ? item.options.map((option) => {
        const optionKey = merchantId && option.externalCode ? `${merchantId}::${option.externalCode}` : '';
        const matchedOption = option.externalCode
          ? (catalog.options?.[optionKey] || catalog.options?.[option.externalCode])
          : null;
        if (!matchedOption) return option;
        return {
          ...option,
          name: matchedOption.name || option.name,
          description: matchedOption.description ?? option.description,
          imageUrl: matchedOption.imageUrl || option.imageUrl,
        };
      })
    : item.options;

  if (!matchedProduct) {
    return projectedOptions !== item.options ? { ...item, options: projectedOptions } : item;
  }
  return {
    ...item,
    name: matchedProduct.name || item.name,
    description: matchedProduct.description ?? item.description,
    imageUrl: matchedProduct.imageUrl || item.imageUrl,
    options: projectedOptions,
  };
};

// Printing must never use the catalog's unscoped aliases: the same PDV code
// may legitimately exist in more than one merchant owned by the tenant.
export const projectIfoodPrintItems = (order = {}, catalog = {}) => {
  const merchantId = getIfoodOrderMerchantId(order);
  const products = catalog?.products || {};
  const options = catalog?.options || {};

  const items = Number(order.amendmentRevision || 0) > 0 && !order.commercialConflict
    ? order.items || [] : getRawIfoodOrderItems(order);
  return items.map((item) => {
    const externalCode = normalizePrintIdentity(item?.externalCode);
    const matchedProduct = merchantId && externalCode
      ? products[`${merchantId}::${externalCode}`]
      : null;
    const projectedOptions = Array.isArray(item?.options)
      ? item.options.map((option) => {
          const optionCode = normalizePrintIdentity(option?.externalCode);
          const matchedOptionCandidate = merchantId && optionCode
            ? options[`${merchantId}::${optionCode}`]
            : null;
          const matchedOption = matchedOptionCandidate?.pdvLinked === true
            ? matchedOptionCandidate
            : null;
          const localName = normalizePrintIdentity(matchedOption?.name);
          return localName ? { ...option, name: localName } : option;
        })
      : item?.options;
    const localName = normalizePrintIdentity(
      matchedProduct?.pdvLinked === true ? matchedProduct.name : ''
    );

    if (!localName && projectedOptions === item?.options) return item;
    return {
      ...item,
      ...(localName ? { name: localName } : {}),
      ...(projectedOptions !== undefined ? { options: projectedOptions } : {}),
    };
  });
};

export const mapInventoryProductToSellableItem = (product, override = {}) => {
  const payload = product?.payload || {};
  const storedItemId = product?.itemId || payload.itemId || '';
  const itemId = storedItemId || String(product?.id || '');
  const itemExternalCode = product?.externalCode || payload.itemExternalCode || null;
  const itemPriceValue = Number(product?.price ?? payload.itemPrice?.value ?? 0);
  const itemType = String(
    payload.itemType
    || payload.type
    || payload.categoryTemplate
    || payload.category?.template
    || product?.itemType
    || ''
  ).trim().toUpperCase();
  const optionGroups = enrichOptionGroupsWithCatalogContext(payload.itemOptionGroups || payload.optionGroups || [], {
    ...payload,
    itemType,
  });

  return {
    ...payload,
    dbId: product?.id,
    source: product?.source || 'manual',
    hasStoredItemId: Boolean(storedItemId),
    merchantId: product?.merchantId || payload.merchantId || '',
    merchantName: product?.merchantName || payload.merchantName || '',
    catalogId: product?.catalogId || payload.catalogId || '',
    catalogContext: product?.catalogContext || payload.catalogContext || '',
    itemId,
    itemExternalCode,
    productId: payload.productId || product?.productId || null,
    itemType,
    type: payload.type || product?.type || itemType || 'DEFAULT',
    categoryTemplate: payload.categoryTemplate || payload.category?.template || product?.categoryTemplate || itemType || '',
    isPizza: product?.isPizza === true || isPizzaCatalogItem({ ...product, ...payload, itemType }),
    pizzaMetadata: payload.pizzaMetadata || payload.metadata?.pizza || product?.pizzaMetadata || null,
    itemName: product?.name || payload.itemName || 'Produto sem nome',
    categoryName: product?.categoryName || payload.categoryName || 'Sem categoria',
    catalogGroupId: product?.catalogGroupId || payload.catalogGroupId || null,
    catalogGroupName: product?.catalogGroupName || product?.categoryName || payload.categoryName || '',
    catalogGroupOrder: Number(product?.catalogGroupOrder ?? payload.catalogGroupOrder ?? 0),
    sortOrder: Number(product?.sortOrder ?? payload.sortOrder ?? 0),
    itemDescription: product?.description || payload.itemDescription || '',
    itemPrice: {
      ...(payload.itemPrice || {}),
      value: itemPriceValue,
    },
    unitPrice: itemPriceValue,
    stock: Number(product?.stock || 0),
    imagePath: payload.imagePath || product?.imagePath || extractCatalogImagePath(payload),
    imageUrl: resolveCatalogImageUrl(product, payload),
    logosUrls: payload.logosUrls || [],
    itemOptionGroups: optionGroups.map((group) => ({
      ...group,
      options: (group.options || []).map((option) => ({
        ...option,
        imagePath: option.imagePath || extractCatalogImagePath(option),
        imageUrl: resolveCatalogImageUrl(option),
        logosUrls: Array.isArray(option.logosUrls) ? option.logosUrls : normalizeImageList(option.logosUrls),
        showInKds: ensureShowInKds(option.showInKds),
      })),
    })),
    itemMinSalePrice: payload.itemMinSalePrice || '',
    itemQuantity: payload.itemQuantity || '',
    itemUnit: payload.itemUnit || '',
    preparationDescription: normalizePreparationDescription(
      override.preparationDescription ?? payload.preparationDescription ?? ''
    ),
    kdsName: normalizeKdsName(override.kdsName ?? payload.kdsName ?? ''),
    showInKds: ensureShowInKds(override.showInKds ?? payload.showInKds),
    showInBalcao: ensureShowInBalcao(override.showInBalcao ?? payload.showInBalcao),
    isActive: product?.isActive !== false,
  };
};

const compactOptionForCache = (option = {}) => ({
  id: option.id || null,
  optionId: getOptionId(option) || null,
  externalCode: option.externalCode || option.itemExternalCode || option.integrationCode || null,
  name: option.name || '',
  description: option.description || '',
  status: option.status || option.availability || '',
  imagePath: option.imagePath || extractCatalogImagePath(option),
  imageUrl: resolveCatalogImageUrl(option),
  logosUrls: Array.isArray(option.logosUrls) ? option.logosUrls.slice(0, 1) : normalizeImageList(option.logosUrls).slice(0, 1),
  price: option.price && typeof option.price === 'object'
    ? { value: Number(resolvePriceValue(option.price) || 0) }
    : { value: Number(resolvePriceValue(option.price) ?? resolvePriceValue(option.unitPrice) ?? 0) },
  contextualPrices: option.contextualPrices || {},
  showInKds: ensureShowInKds(option.showInKds),
});

const compactGroupForCache = (group = {}) => {
  const limits = normalizeOptionGroupLimits(group);
  return {
    id: group.id || null,
    optionGroupId: getOptionGroupId(group),
    name: group.name || '',
    description: group.description || '',
    ...limits,
    optionGroupType: group.optionGroupType || group.type || normalizeOptionGroupRole(group),
    pizzaRole: normalizeOptionGroupRole(group),
    options: (group.options || []).map(compactOptionForCache),
  };
};

const compactSellableItemForCache = (product = {}) => ({
  dbId: product.dbId || null,
  source: product.source || 'manual',
  hasStoredItemId: product.hasStoredItemId === true,
  merchantId: product.merchantId || '',
  merchantName: product.merchantName || '',
  catalogId: product.catalogId || '',
  catalogContext: product.catalogContext || '',
  itemId: product.itemId || '',
  productId: product.productId || null,
  itemExternalCode: product.itemExternalCode || null,
  itemName: product.itemName || 'Produto sem nome',
  itemType: product.itemType || product.type || '',
  type: product.type || product.itemType || '',
  isPizza: isPizzaCatalogItem(product),
  pizzaMetadata: product.pizzaMetadata || null,
  categoryName: product.categoryName || 'Sem categoria',
  catalogGroupId: product.catalogGroupId || null,
  catalogGroupName: product.catalogGroupName || product.categoryName || 'Sem categoria',
  catalogGroupOrder: Number(product.catalogGroupOrder || 0),
  sortOrder: Number(product.sortOrder || 0),
  categoryTemplate: product.categoryTemplate || '',
  itemDescription: product.itemDescription || '',
  itemPrice: {
    ...(product.itemPrice || {}),
    value: Number(product.itemPrice?.value || product.unitPrice || 0),
  },
  unitPrice: Number(product.itemPrice?.value || product.unitPrice || 0),
  stock: Number(product.stock || 0),
  imageUrl: resolveCatalogImageUrl(product),
  imagePath: product.imagePath || extractCatalogImagePath(product),
  logosUrls: Array.isArray(product.logosUrls) ? product.logosUrls.slice(0, 1) : [],
  itemOptionGroups: (product.itemOptionGroups || []).map(compactGroupForCache),
  itemMinSalePrice: product.itemMinSalePrice || '',
  itemQuantity: product.itemQuantity || '',
  itemUnit: product.itemUnit || '',
  preparationDescription: normalizePreparationDescription(product.preparationDescription),
  kdsName: normalizeKdsName(product.kdsName),
  showInKds: ensureShowInKds(product.showInKds),
  showInBalcao: ensureShowInBalcao(product.showInBalcao),
  isActive: product.isActive !== false,
});

export const buildCatalogFromProducts = (items = []) => {
  const catalog = { products: {}, options: {} };

  items.forEach((product) => {
    const productCode = product.itemExternalCode || product.itemId;
    if (productCode) {
      const productEntry = {
        externalCode: productCode,
        name: product.itemName,
        merchantId: product.merchantId || '',
        merchantName: product.merchantName || '',
        catalogId: product.catalogId || '',
        description: product.itemDescription,
        preparationDescription: normalizePreparationDescription(product.preparationDescription),
        kdsName: normalizeKdsName(product.kdsName),
        imageUrl: resolveCatalogImageUrl(product),
        price: Number(product.itemPrice?.value || 0),
        showInKds: ensureShowInKds(product.showInKds),
        showInBalcao: ensureShowInBalcao(product.showInBalcao),
      };
      const merchantScopedKey = product.merchantId ? `${product.merchantId}::${productCode}` : '';
      if (merchantScopedKey) {
        catalog.products[merchantScopedKey] = productEntry;
      }
      if (!catalog.products[productCode]) {
        catalog.products[productCode] = productEntry;
      }
    }

    (product.itemOptionGroups || []).forEach((group) => {
      (group.options || []).forEach((option) => {
        if (!option.externalCode) return;
        const optionEntry = {
          externalCode: option.externalCode,
          name: option.name,
          merchantId: product.merchantId || '',
          merchantName: product.merchantName || '',
          catalogId: product.catalogId || '',
          description: option.description,
          imagePath: option.imagePath || extractCatalogImagePath(option),
          imageUrl: resolveCatalogImageUrl(option),
          showInKds: ensureShowInKds(option.showInKds),
        };
        const merchantScopedKey = product.merchantId ? `${product.merchantId}::${option.externalCode}` : '';
        if (merchantScopedKey) {
          catalog.options[merchantScopedKey] = optionEntry;
        }
        if (!catalog.options[option.externalCode]) {
          catalog.options[option.externalCode] = optionEntry;
        }
      });
    });
  });

  return catalog;
};

export const persistSharedCatalogData = (items = [], { session = getCatalogStorageSession() } = {}) => {
  if (typeof window === 'undefined') return;
  if (!isCatalogStorageSessionCurrent(session)) return false;
  const normalizedItems = (items || []).map((product) => ({
    ...product,
    imageUrl: resolveCatalogImageUrl(product),
    unitPrice: Number(product.itemPrice?.value || product.unitPrice || 0),
  }));
  const compactItems = normalizedItems.map(compactSellableItemForCache);
  const catalog = buildCatalogFromProducts(compactItems);
  writeJsonToLocalStorage(CATALOG_PRODUCT_CACHE_KEY, catalog, SHARED_CATALOG_MAX_JSON_CHARACTERS, session);
  writeJsonToLocalStorage(CATALOG_SELLABLE_ITEMS_CACHE_KEY, compactItems, SHARED_CATALOG_MAX_JSON_CHARACTERS, session);
  return isCatalogStorageSessionCurrent(session);
};

export const readSharedProductCatalog = () => readJsonFromLocalStorage(CATALOG_PRODUCT_CACHE_KEY, {});
export const readSharedSellableItemsCache = () => readJsonFromLocalStorage(CATALOG_SELLABLE_ITEMS_CACHE_KEY, []);

export const updateSharedCatalogProduct = (product = {}, { append = false } = {}) => {
  const current = readSharedSellableItemsCache();
  if (!Array.isArray(current) || !current.length) return false;
  const productId = Number(product.dbId || 0);
  const merchantId = String(product.merchantId || '');
  const externalCode = String(product.itemExternalCode || product.itemId || '');
  let replaced = false;
  const next = current.map((item) => {
    const sameDatabaseId = productId && Number(item.dbId || 0) === productId;
    const sameCatalogIdentity = externalCode
      && String(item.merchantId || '') === merchantId
      && String(item.itemExternalCode || item.itemId || '') === externalCode;
    if (!sameDatabaseId && !sameCatalogIdentity) return item;
    replaced = true;
    return product;
  });
  if (replaced) persistSharedCatalogData(next);
  else if (append) persistSharedCatalogData([...current, product]);
  return replaced || append;
};

export const fetchInventoryProductsWithOverrides = async (apiUrl = getApiUrl(), { signal, persist = true } = {}) => {
  const ownerSession = getCatalogStorageSession();
  const [items, overrides] = await Promise.all([
    fetchInventoryProducts(apiUrl, { signal }),
    fetchCatalogOverridesSafely(apiUrl, { signal }),
  ]);

  const overridesMap = (overrides || []).reduce((acc, item) => {
    if (item?.externalCode) {
      const merchantKey = item.merchantId ? `${item.merchantId}::${item.externalCode}` : '';
      if (merchantKey) acc[merchantKey] = item;
      acc[item.externalCode] = item;
    }
    return acc;
  }, {});

  const normalized = (items || []).map((product) => {
    const key = product.externalCode || product.itemId || product.id;
    const merchantScopedKey = product.merchantId && product.externalCode ? `${product.merchantId}::${product.externalCode}` : '';
    return mapInventoryProductToSellableItem(product, overridesMap[merchantScopedKey] || overridesMap[key] || {});
  });

  if (persist && isCatalogStorageSessionCurrent(ownerSession)) {
    persistSharedCatalogData(normalized, { session: ownerSession });
  }
  return normalized;
};

export const queueCatalogOverrideForSync = (override) => {
  if (!override?.externalCode) return;
  const current = readJsonFromLocalStorage(CATALOG_PENDING_OVERRIDES_KEY, {});
  const key = override.merchantId ? `${override.merchantId}::${override.externalCode}` : override.externalCode;
  current[key] = override;
  writeJsonToLocalStorage(CATALOG_PENDING_OVERRIDES_KEY, current);
};

export const removePendingCatalogOverride = (externalCode, merchantId = '') => {
  if (!externalCode) return;
  const current = readJsonFromLocalStorage(CATALOG_PENDING_OVERRIDES_KEY, {});
  delete current[externalCode];
  if (merchantId) {
    delete current[`${merchantId}::${externalCode}`];
  }
  writeJsonToLocalStorage(CATALOG_PENDING_OVERRIDES_KEY, current);
};

export const syncLocalCatalogOverridesToBackend = async (apiUrl = getApiUrl()) => {
  if (typeof window === 'undefined') return 0;
  const ownerSession = getCatalogStorageSession();
  if (!isCatalogStorageSessionCurrent(ownerSession)) return 0;

  const storedCatalog = readJsonFromLocalStorage(CATALOG_PRODUCT_CACHE_KEY, {});

  const products = Object.entries(storedCatalog.products || {});

  const catalogCandidates = products
    .map(([catalogKey, product]) => ({
      syncKey: product?.merchantId && product?.externalCode ? `${product.merchantId}::${product.externalCode}` : catalogKey,
      externalCode: product?.externalCode || catalogKey,
      merchantId: product?.merchantId || '',
      itemName: product?.name || null,
      preparationDescription: normalizePreparationDescription(product?.preparationDescription),
      kdsName: normalizeKdsName(product?.kdsName),
      showInKds: ensureShowInKds(product?.showInKds),
      showInBalcao: ensureShowInBalcao(product?.showInBalcao),
    }))
    .filter((item) => item.externalCode && (item.preparationDescription || item.kdsName || item.showInKds === false || item.showInBalcao === false));

  const pendingCandidates = Object.values(readJsonFromLocalStorage(CATALOG_PENDING_OVERRIDES_KEY, {}));
  const candidatesMap = {};
  [...catalogCandidates, ...pendingCandidates].forEach((item) => {
    if (item?.externalCode) {
      const key = item.merchantId ? `${item.merchantId}::${item.externalCode}` : item.syncKey || item.externalCode;
      candidatesMap[key] = item;
    }
  });

  const candidates = Object.values(candidatesMap);
  if (!candidates.length) return 0;

  const failures = {};
  await Promise.all(candidates.map(async (item) => {
    try {
      const response = await apiFetch(`${apiUrl}/api/products/catalog-overrides/${encodeURIComponent(item.externalCode)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(item),
      });

      if (!response.ok) {
        const key = item.merchantId ? `${item.merchantId}::${item.externalCode}` : item.externalCode;
        failures[key] = item;
      }
    } catch {
      const key = item.merchantId ? `${item.merchantId}::${item.externalCode}` : item.externalCode;
      failures[key] = item;
    }
  }));

  if (isCatalogStorageSessionCurrent(ownerSession)) {
    writeJsonToLocalStorage(CATALOG_PENDING_OVERRIDES_KEY, failures, undefined, ownerSession);
  }

  return candidates.length;
};

const fetchSellableItems = async (apiUrl) => {
  const { merchantId, catalogId } = await resolveIfoodCatalogContext(apiUrl);
  const response = await apiFetch(
    `${apiUrl}/api/ifood/catalog/merchants/${merchantId}/catalogs/${catalogId}/sellableItems`
  );
  if (!response.ok) {
    throw new Error(`Erro HTTP ${response.status}: ${response.statusText}`);
  }
  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Erro ao carregar itens vendáveis');
  }
  return (data.items || []).map((item) => ({
    ...item,
    merchantId,
    catalogId,
  }));
};

export const fetchSellableItemsWithOverrides = async (apiUrl = getApiUrl()) => {
  const [items, overrides] = await Promise.all([
    fetchSellableItems(apiUrl),
    fetchCatalogOverridesSafely(apiUrl),
  ]);
  const storedCatalog = readJsonFromLocalStorage(CATALOG_PRODUCT_CACHE_KEY, {});
  const localProducts = storedCatalog.products || {};

  const overridesMap = overrides.reduce((acc, item) => {
    if (item.externalCode) {
      const merchantKey = item.merchantId ? `${item.merchantId}::${item.externalCode}` : '';
      if (merchantKey) acc[merchantKey] = item;
      acc[item.externalCode] = item;
    }
    return acc;
  }, {});

  return items.map((product) => {
    const key = product.itemExternalCode || product.itemId;
    const merchantScopedKey = product.merchantId && key ? `${product.merchantId}::${key}` : '';
    const override = overridesMap[merchantScopedKey] || overridesMap[key] || {};
    const localProduct = localProducts[merchantScopedKey] || localProducts[key] || {};

    return {
      ...product,
      ...override,
      imageUrl: resolveCatalogImageUrl(product),
      itemOptionGroups: (product.itemOptionGroups || []).map((group) => ({
        ...group,
        options: (group.options || []).map((option) => ({
          ...option,
          imagePath: option.imagePath || extractCatalogImagePath(option),
          imageUrl: resolveCatalogImageUrl(option),
          logosUrls: Array.isArray(option.logosUrls) ? option.logosUrls : normalizeImageList(option.logosUrls),
        })),
      })),
      preparationDescription: normalizePreparationDescription(
        override.preparationDescription ?? localProduct.preparationDescription ?? product.preparationDescription ?? ''
      ),
      kdsName: normalizeKdsName(override.kdsName ?? localProduct.kdsName ?? product.kdsName ?? ''),
      showInKds: ensureShowInKds(override.showInKds ?? localProduct.showInKds ?? product.showInKds),
      showInBalcao: ensureShowInBalcao(override.showInBalcao ?? localProduct.showInBalcao ?? product.showInBalcao),
    };
  });
};
